/**
 * useLatestExtensionVersion — TanStack Query hook for the public
 * `GET /api/web/v2/extension/latest` endpoint.
 *
 * Consumed by both the extension popup widget (Phase 05) and the web
 * Outreach > Sequences card. The endpoint returns the per-stage current
 * snapshot derived from `releases.json` (the extension-observability
 * Lambda's S3 object) — `current.available` (what Google is serving) +
 * `current.coming_soon` (what was last submitted but not yet promoted).
 *
 * Both fields are `null`-tolerant by design:
 *
 *   - `available: null` indefinitely during CWS pre-launch state
 *   - `coming_soon: null` when no draft is pending
 *
 * Network/5xx failures resolve to `data: undefined` (no throw at the
 * hook level — see `retry: false` + `throwOnError: false`). The popup
 * and Sequences card both treat the missing data as "hide the warning /
 * hide the card" instead of an error UI per the PRD's "fail silently"
 * requirement.
 *
 * The 5-minute `staleTime` mirrors the API's 60-second in-process cache
 * + the Lambda's 5-minute cadence — total upper-bound staleness is
 * ~6 minutes, well under the human-visible window for a publish.
 */
import { useQuery } from "@tanstack/react-query";
import type { ExtensionLatestResponse } from "@nu-assist/contracts";
import type { ExtensionName } from "../domain/extension";

const STALE_TIME_MS = 5 * 60 * 1000;
const DEFAULT_API_BASE = "https://api.local.nu-assist.com";

// `ExtensionName` is defined in the leaf `domain/extension.ts` so the pure
// `stage/cwsLinks.ts` table can share it without importing this React module.
// Re-exported here because this was its original home and callers (plus
// `hooks/index.ts`) import it from this path.
export type { ExtensionName };

export interface UseLatestExtensionVersionOptions {
  /** API base URL. Default: `import.meta.env.VITE_API_URL` or local. */
  apiBaseUrl?: string;
  /** Override the 5-minute staleTime (tests use 0). */
  staleTime?: number;
  /**
   * Which extension's release data to request (`?extension=`). Default
   * `nucce`; the Baby Chops popup + web page pass `baby-chops` so they get
   * their own listing's snapshot from the extension-aware endpoint instead
   * of NUCCE's.
   */
  extension?: ExtensionName;
}

/**
 * Resolve the API base URL.
 *
 * Precedence:
 *   1. Explicit `opts.apiBaseUrl` (the extension popup passes its
 *      stage-resolved URL here so dev/prod bundles don't fall through
 *      to the local default).
 *   2. `import.meta.env.VITE_API_URL` (set by each app's vite.config).
 *   3. `DEFAULT_API_BASE` (local dev safety net).
 */
function resolveApiBase(opts: UseLatestExtensionVersionOptions): string {
  if (opts.apiBaseUrl) return opts.apiBaseUrl;
  const fromEnv = import.meta.env?.VITE_API_URL;
  if (typeof fromEnv === "string" && fromEnv.length > 0) return fromEnv;
  return DEFAULT_API_BASE;
}

/**
 * Returns the current `available` + `coming_soon` snapshot for the
 * extension listing on the active stage.
 *
 * Failure modes (all collapsed to `data: undefined`):
 *   - network error / CORS
 *   - 5xx response
 *   - 200 with malformed JSON
 *
 * The hook never throws — it's the consumer's responsibility to read
 * `data?.current?.available?.manifest_version` defensively.
 */
export function useLatestExtensionVersion(
  opts: UseLatestExtensionVersionOptions = {},
) {
  const apiBase = resolveApiBase(opts);
  const extension = opts.extension ?? "nucce";
  const url = `${apiBase}/api/web/v2/extension/latest?extension=${extension}`;

  return useQuery<ExtensionLatestResponse | null>({
    queryKey: ["extension", "latest", apiBase, extension],
    staleTime: opts.staleTime ?? STALE_TIME_MS,
    // A network blip on this surface is non-blocking by design (popup
    // shows installed version only; Sequences card hides). Retrying
    // would just slow the popup paint; the 5-min staleTime + the
    // user's next session is the retry mechanism.
    retry: false,
    // Suppress TanStack Query's default isError surface — consumers
    // already render the null-data branch.
    throwOnError: false,
    queryFn: async (): Promise<ExtensionLatestResponse | null> => {
      try {
        const response = await fetch(url, {
          method: "GET",
          headers: { Accept: "application/json" },
        });
        if (!response.ok) return null;
        const body = (await response.json()) as ExtensionLatestResponse;
        return body;
      } catch {
        return null;
      }
    },
  });
}
